import moment from 'moment';
import { generateScheduleItemLabel, generateStudentScheduleItemLabel, generateAvailabilityItemLabel } from './schedulingLabelGenerator';
import { mapStudentSchedule } from './scheduleUtils.js';

const getTeacherName = (teacher) => {
    return teacher && teacher.id
        ? `${teacher.firstName || ''} ${teacher.lastName ? teacher.lastName.charAt(0) : ''}`
        : 'Unassigned';
}

const updateRowTotalCountByType = (items, rows) => {
    const rowCounts = {};

    // Count the items by type
    items.forEach((item) => {
        const { rowId } = item;
        if (!rowCounts[rowId]) {
            rowCounts[rowId] = { total: 0, items: [] };  // Initialize the total counter
        }

        rowCounts[rowId].total++;  // Increment the total counter for each item
        rowCounts[rowId].items.push(item);
    });

    // Update the row totals
    rows.forEach((row) => {
        const { id } = row;
        if (rowCounts[id]) {
            row.totalCount = rowCounts[id].total;  // Store the total count separately
            row.items = rowCounts[id].items;  // Store the total count separately
        } else {
            row.totalCount = 0;
            row.items = [];
        }
    });
}


const generateGanttRows = (groups) => {

    // Sort groups so that 'Unassigned' is first, then 'Teachers', then 'Classrooms'
    groups.sort((a, b) => {
        const aPriority = Object.prototype.hasOwnProperty.call(a, 'teacherId')
            ? 1 // Teacher
            : Object.prototype.hasOwnProperty.call(a, 'classroomId')
                ? 2 // Classroom
                : 0; // Unassigned

        const bPriority = Object.prototype.hasOwnProperty.call(b, 'teacherId')
            ? 1 // Teacher
            : Object.prototype.hasOwnProperty.call(b, 'classroomId')
                ? 2 // Classroom
                : 0; // Unassigned

        return aPriority - bPriority;
    });

    let idCounter = 1;
    return groups.flatMap((group, index) => {
        const groupInternalId = `#${index + 1}`;

        let groupName = 'Unassigned';
        let type;
        let entityId = null;
        let teacherFirstName = null;
        let teacherLastName = null;

        if (Object.prototype.hasOwnProperty.call(group, 'teacherId')) {
            groupName = getTeacherName(group.teacher);
            if(group.teacher && group.teacher.id){
                teacherFirstName = group.teacher.firstName;
                teacherLastName = group.teacher.lastName;
            }

            type = 'teacher';
            entityId = group.teacherId;
        } else if (Object.prototype.hasOwnProperty.call(group, 'classroomId')) {
            groupName = group.classroom.roomNumber;
            type = 'classroom';
            entityId = group.classroomId;
        }

        const rows = [];

        // Check if teacher schedule or availability exists for teacher groups
        if (type === 'teacher' && (group.schedules.length > 0 || group.availability.length > 0)) {
            rows.push({
                id: idCounter++,
                internalId: `${groupInternalId}.availability`,
                name: groupName,
                teacherFirstName,
                teacherLastName,
                type: "teacher-availability",
                entityId,
                groupType: 'teacher',
                style: ''
            });
            rows.push({
                id: idCounter++,
                internalId: `${groupInternalId}.schedule`,
                name: groupName,
                teacherFirstName,
                teacherLastName,
                type: "schedule",
                entityId,
                groupType: 'teacher',
                style: ''
            });
        }

        // Check if student schedules exists
        if (group.students.length > 0 && type !== 'classroom') {
            rows.push({
                id: idCounter++,
                internalId: `${groupInternalId}.student-schedule`,
                name: groupName,
                teacherFirstName,
                teacherLastName,
                type: "student-schedule",
                entityId,
                groupType: 'teacher',
                style: ''
            });
        }

        // Create a row each classroom
        if (type === 'classroom') {
            rows.push({
                id: idCounter++,
                internalId: `${groupInternalId}.classroom-teacher`,
                name: groupName,
                type: "schedule",
                groupType: 'classroom',
                entityId,
                style: ''
            });
            rows.push({
                id: idCounter++,
                internalId: `${groupInternalId}.classroom-student`,
                name: groupName,
                type: "student-schedule",
                groupType: 'classroom',
                entityId,
                style: ''
            });
        }

        // set row's group ID
        rows.forEach(r => r.groupId = r.groupType == 'classroom' ? `c-${r.entityId}` : r.entityId)

        return rows;
    });
}


const intersectScheduleAvailability = (timeStart, timeEnd, schedule) => {
    let items = [];

    if (schedule.start.isBefore(timeEnd) && schedule.end.isAfter(timeStart)) {
        // There is a schedule within the availability

        // Create an item for the time before the schedule
        if (schedule.start.isAfter(timeStart)) {
            items.push({
                start: timeStart,
                end: schedule.start
            });
        }

        // Update timeStart to be after the schedule
        timeStart = schedule.end;
    }

    return { items, timeStart };
}

const processAvailability = (availability, rowId, schedules, generateItem, groupBy = 'day') => {
    const { id, teacherId, startTime, endTime, date } = availability;

    let timeStart = moment(`${date} ${startTime}`);
    let timeEnd = moment(`${date} ${endTime}`);

    console.info('need to implement group by for availability: ' + groupBy)
    // let timeStart = groupBy === 'day' ? start.startOf('day') : start;
    // let timeEnd = groupBy === 'day' ? end.endOf('day') : end;


    let items = [];

    schedules.forEach(schedule => {
        let result = intersectScheduleAvailability(timeStart, timeEnd, schedule);

        timeStart = result.timeStart;

        result.items.forEach(time => {
            const item = generateItem(id, teacherId, rowId, 'availability', 'Availability', { background: "#D3D3D3" }, time);
            const itemLabel = generateAvailabilityItemLabel(item);
            item.label = itemLabel;
            items.push(
                item
            );
        });
    });

    // Create an item for the time after the last schedule within the availability
    if (timeStart.isBefore(timeEnd)) {
        const item = generateItem(id, teacherId, rowId, 'availability', 'Availability', { background: "#D3D3D3" }, { start: timeStart, end: timeEnd });
        const itemLabel = generateAvailabilityItemLabel(item);
        item.label = itemLabel;
        items.push(
            item
        );
    }

    return items;
}

const generateTeacherAvailabilityItems = (teacher, rowId, editable, groupBy = 'day') => {
    if (!teacher.availability || !teacher.schedules) {
        return [];
    }

    const schedules = teacher.schedules.map(s => ({
        start: moment(s.startTime),
        end: moment(s.endTime),
    })).sort((a, b) => a.start - b.start);  // Sort by start time

    const generateItem = (id, teacherId, rowId, type, label, style, time) => ({
        id,
        teacherId,
        rowId,
        type,
        label,
        style,
        time,
        moveable: editable,
        resizable: editable,
    });

    let items = [];

    teacher.availability.forEach(availability => {
        items.push(...processAvailability(availability, rowId, schedules, generateItem, groupBy));
    });

    return items;
}

const generateScheduleItems = (group, row, groupBy = 'day') => {
    const teacherName = getTeacherName(group.teacher);

    let teacherFirstName = null;
    let teacherLastName = null;
    if(group.teacher){
        teacherFirstName = group.teacher.firstName;
        teacherLastName = group.teacher.lastName;
    }

    const rowId = row.id;
    const groupType = row.groupType;

    // Group schedules by date or hour
    const groupedSchedules = group.schedules.reduce((acc, schedule) => {
        //set teacher name
        if(group.teacher){
            schedule.teacherFirstName = teacherFirstName;
            schedule.teacherLastName = teacherLastName;
        }

        let groupByValue = moment(schedule.startTime).format('YYYY-MM-DD');
        if (groupBy !== 'day') {
            groupByValue = moment(schedule.startTime).format('YYYY-MM-DD HH');
        }
        if (!acc[groupByValue]) {
            acc[groupByValue] = [];
        }
        acc[groupByValue].push(schedule);
        return acc;
    }, {});

    // Generate items
    return Object.entries(groupedSchedules).map(([groupByValue, schedules], index) => {
        let timeStart;
        let timeEnd;

        if (groupBy === 'day' || schedules.length === 1) {
            timeStart = groupBy === 'day' ? moment(groupByValue).startOf('day') : moment(schedules[0].startTime);
            timeEnd = groupBy === 'day' ? moment(groupByValue).endOf('day') : moment(schedules[0].endTime);
        } else {
            // If there are more than one schedule entries, use the earliest start time and the latest end time
            timeStart = schedules.reduce((earliest, schedule) =>
                moment(schedule.startTime).isBefore(earliest) ? moment(schedule.startTime) : earliest, moment(schedules[0].startTime)
            );

            timeEnd = schedules.reduce((latest, schedule) =>
                moment(schedule.endTime).isAfter(latest) ? moment(schedule.endTime) : latest, moment(schedules[0].endTime)
            );

        }



        const forClassroom = groupType == 'classroom';
        return {
            index,
            teacherId: group.teacherId,
            teacherName: teacherName,
            teacherFirstName, 
            teacherLastName, 
            rowId: rowId,
            type: 'schedule',
            label: generateScheduleItemLabel(schedules, forClassroom),
            data: schedules.length === 1 ? schedules[0] : undefined, //backward compatible
            details: [...schedules],
            style: { background: "#24abf2" },
            time: {
                start: timeStart,
                end: timeEnd,
            },
            moveable: false,
            resizable: false,
        };
    });
}



const generateStudentScheduleItems = (teacherGroup, rowId, groupBy = 'day') => {
    const teacherName = getTeacherName(teacherGroup.teacher);

    if (!teacherGroup.students || teacherGroup.students.length < 1) {
        return [];
    }

    let teacherFirstName = null;
    let teacherLastName = null;
    if(teacherGroup.teacher){
        teacherFirstName = teacherGroup.teacher.firstName;
        teacherLastName = teacherGroup.teacher.lastName;
    }

    // Group schedules by date or hour
    const groupedSchedules = teacherGroup.students.reduce((acc, schedule) => {
        let groupByValue = moment(schedule.startTime).format('YYYY-MM-DD');
        if (groupBy !== 'day') {
            groupByValue = moment(schedule.startTime).format('YYYY-MM-DD HH');
        }
        if (!acc[groupByValue]) {
            acc[groupByValue] = [];
        }
        acc[groupByValue].push(schedule);
        return acc;
    }, {});

    // Generate items
    return Object.entries(groupedSchedules).map(([groupByValue, schedules], index) => {
        // Include details of each schedule entry for auditing
        const details = schedules.map(mapStudentSchedule);

        let timeStart;
        let timeEnd;

        if (groupBy === 'day' || schedules.length === 1) {
            timeStart = groupBy === 'day' ? moment(groupByValue).startOf('day') : moment(schedules[0].startTime);
            timeEnd = groupBy === 'day' ? moment(groupByValue).endOf('day') : moment(schedules[0].endTime);
        } else {
            // If there are more than one schedule entries, use the earliest start time and the latest end time
            timeStart = schedules.reduce((earliest, schedule) =>
                moment(schedule.startTime).isBefore(earliest) ? moment(schedule.startTime) : earliest, moment(schedules[0].startTime)
            );

            timeEnd = schedules.reduce((latest, schedule) =>
                moment(schedule.endTime).isAfter(latest) ? moment(schedule.endTime) : latest, moment(schedules[0].endTime)
            );
        }
        return {
            index,
            teacherId: teacherGroup.teacherId,
            teacherName: teacherName,
            teacherFirstName,
            teacherLastName,
            classroomId: teacherGroup.classroomId,
            classroomName: teacherGroup.classroom ? teacherGroup.classroom.roomNumber : null,
            rowId: rowId,
            type: 'student-schedule',
            label: generateStudentScheduleItemLabel(details),
            details: details,
            style: { background: "#f2a024" },
            time: {
                start: timeStart,
                end: timeEnd,
            },
            // moveable: groupBy === 'hour',
            // resizable: groupBy === 'hour',
            moveable: false,
            resizable: false,
        };
    });
}

const generateGanttItem = (dataGroup, row, groupBy = 'day') => {

    if (!dataGroup || !row || !row.id) {
        return []
    }

    // const editable = groupBy == 'hour' ? true : false;
    const editable = false;

    const items = [];
    const rowId = row.id;
    const rowType = row.type;

    switch (rowType) {
        case 'schedule':
            items.push(...generateScheduleItems(dataGroup, row, groupBy));
            break;
        case 'teacher-availability':
            items.push(...generateTeacherAvailabilityItems(dataGroup, rowId, editable, groupBy));
            break;
        case 'student-schedule':
        case 'classroom':
            items.push(...generateStudentScheduleItems(dataGroup, rowId, groupBy));
            break
    }

    return items;
}


const generateGanttItems = (dataGroups, ganttRows, groupBy = 'day') => {
    let items = [];

    for (let row of ganttRows) {
        const rowType = row.name == 'Unassigned' ? 'Unassigned' : row.type
        const groupType = row.groupType;
        let dataGroup = null;
        switch (rowType) {
            case 'schedule':
            case 'teacher-availability':
                if(groupType == 'classroom'){
                    dataGroup = dataGroups.find(g => g.classroomId == row.entityId)
                }
                else{
                    dataGroup = dataGroups.find(g => g.teacherId == row.entityId)
                }
                break;
            case 'student-schedule':
                if(groupType == 'classroom'){
                    dataGroup = dataGroups.find(g => g.classroomId == row.entityId)
                }
                else{
                    dataGroup = dataGroups.find(g => g.teacherId == row.entityId)
                }
                break;
            case 'Unassigned':
                dataGroup = dataGroups.find(g => g.unassigned)
                break;
        }

        items = items.concat(generateGanttItem(dataGroup, row, groupBy));
    }

    return items;
}

export {
    generateGanttItems,
    generateGanttItem,
    generateGanttRows,
    updateRowTotalCountByType,
    getTeacherName
}
